import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ChangeDetectionStrategy
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { TableSubPlanAssociationBadgesMapper } from 'src/app/mapper/table/table-subplan-association-badges.mapper';
import { BadgePageable } from 'src/app/models/badge/badge-pageable';
import { SubscriptionPlanModel } from 'src/app/models/subscription-plan/SubscriptionPlanModel';
import { UserModel } from 'src/app/models/users/UserModel';
import { VehicleModel } from 'src/app/models/vehicles/VehicleModel';
import { BadgeRepo as BadgeRepo2 } from 'src/app/repositories/badge-repo';
import { BadgeRepo } from 'src/app/repositories/badge/repo/badge-repo';
import { SubscriptionRepo } from 'src/app/repositories/subscription-repo';
import { UserRepo } from 'src/app/repositories/user-repo';
import { VehicleRepo } from 'src/app/repositories/vehicle-repo';
import { AlertService } from 'src/app/services';
import { IconType } from 'src/app/tinea-components/icon/enum/icon-type';
import { UiLoadingModel } from 'src/app/tinea-components/loading/models/UiLoadingModel';
import { LinkClickSateType } from 'src/app/tinea-components/paging/enum/link-click-state-type';
import { PagingModel } from 'src/app/tinea-components/paging/models/input/paging-model';
import { TableModel } from 'src/app/tinea-components/table/models/input/table-model';
import { UiIcon } from 'src/app/tinea-components/table/models/input/ui-icon';
import {
  AssignAuthSupportRequestBody,
  AssignVehiclesRequestBody,
  EnergyType, SubscribeUsersRequestBody, SubscriptionUser, User, UserList,

  Vehicle,
  VehicleCreate,
  VehiclePagedList,
  VehicleType
} from 'src/swagger-gen/web-secured/models';
import { SubPlanAssociationItemType } from './enums/subPlanAssociationItemType';
import { SubPlanAssociationBadgeModel } from './models/subPlanAssociationBadgeModel';
import { SubPlanAssociationItemModel } from './models/subPlanAssociationItemModel';
import { SubPlanAssociationUserBadgeModel } from './models/subPlanAssociationUserBadgeModel';

@Component({
    selector: 'app-sub-plan-associations',
    templateUrl: './sub-plan-associations.component.html',
    styleUrls: ['./sub-plan-associations.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'sub-plan-associations' },
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SubPlanAssociationsComponent implements OnInit, OnChanges {
  @Input() itemType: SubPlanAssociationItemType;
  @Input() subscriptionPlan: SubscriptionPlanModel;
  @Input() defaultItem: UserModel | VehicleModel;
  @Output() dataHasChanged = new EventEmitter<boolean>();

  readonly placeholderUser = 'component.subPlanAssociations.user.placeholder';
  readonly placeholderVehicle =
    'component.subPlanAssociations.vehicle.placeholder';
  readonly placeholderBadge = 'component.subPlanAssociations.badge.placeholder';
  readonly titleTableBadge = 'component.subPlanAssociations.tableBadges.title';
  errorAssignBadgeMsg =
    'component.subPlanAssociations.badge.error.addBadge';
  readonly DELAY_SEARCH_ITEM_AFTER_KEYUP = 500;

  itemPlaceholder: string;
  badgesPlaceholder: string;
  itemTypes = SubPlanAssociationItemType;

  // user or vehicle
  searchedItem = new UntypedFormControl('', [Validators.required]);
  searchedBadge = new UntypedFormControl('', [Validators.required]);

  items: SubPlanAssociationItemModel[];
  badges: SubPlanAssociationBadgeModel[]; // auto-completion
  actualBadges: SubPlanAssociationUserBadgeModel[] = [];
  allActualBadges: SubPlanAssociationUserBadgeModel[];
  itemListloadingModel: UiLoadingModel = new UiLoadingModel();
  badgeListloadingModel: UiLoadingModel = new UiLoadingModel();
  assignBadgeListloadingModel: UiLoadingModel = new UiLoadingModel();
  addItemToSubPlanLoadingModel: UiLoadingModel = new UiLoadingModel();
  userBadgesloadingModel: UiLoadingModel = new UiLoadingModel();
  errorGetItemBadges: boolean;
  errorAddItemToSubPlan: boolean;
  errorGettingItems: boolean;
  errorGettingBadges: boolean;
  errorAssignBadge: boolean;
  errorUnassignBadge: boolean;
  currentItem: SubPlanAssociationItemModel;
  valueWrited: string;

  itemOffsetList = 0;
  itemLimitList = 15;
  badgeOffsetList = 0;
  badgeLimitList = 15;
  timeoutSearchItem: any;

  badgeTable: TableModel;
  actualBadgesOffsetList = 0;
  actualBadgesLimitList = 8;
  paging: PagingModel;

  constructor(
    private translate: TranslateService,
    private userRepo: UserRepo,
    private subscriptionRepo: SubscriptionRepo,
    private vehicleRepo: VehicleRepo,
    private tableSubPlanAssociationBadgesMapper: TableSubPlanAssociationBadgesMapper,
    private badgeRepo: BadgeRepo,
    private badgeRepo2: BadgeRepo2
  ) {
    this.itemPlaceholder = null;
    this.errorGettingItems = null;
    this.badgesPlaceholder = translate.instant(this.placeholderBadge);
    this.searchedBadge.disable();
  }

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemType && changes.itemType.currentValue) {
      this.setItemPlaceholder();
    }
    if (changes.defaultItem && changes.defaultItem.currentValue) {
      const item: UserModel | VehicleModel = changes.defaultItem.currentValue;
      this.setDefaultItem(item);
    }
  }

  setDefaultItem(item: UserModel | VehicleModel) {
    const itemModel = new SubPlanAssociationItemModel(
      this.itemType,
      item,
      this.subscriptionPlan
    );
    this.searchedItem.patchValue(itemModel);
    this.searchedItem.disable();
    this.onClickItemOption(itemModel);
  }

  setItemPlaceholder(): void {
    if (this.itemType === SubPlanAssociationItemType.USER) {
      this.itemPlaceholder = this.translate.instant(this.placeholderUser);
    } else if (this.itemType === SubPlanAssociationItemType.VEHICLE) {
      this.itemPlaceholder = this.translate.instant(this.placeholderVehicle);
    }
  }

  getItemBadges(itemUid: string): void {
    this.userBadgesloadingModel.isLoading = true;
    if (this.itemType === SubPlanAssociationItemType.USER) {
      this.subscriptionRepo
        .getUserOfSubscription(this.subscriptionPlan.uid, itemUid)
        .toPromise()
        .then((res: SubscriptionUser) => {
          this.errorGetItemBadges = false;
          this.userBadgesloadingModel.isLoading = false;
          this.afterGetItemBadges(res);
        })
        .catch((error) => {
          this.errorGetItemBadges = true;
          this.userBadgesloadingModel.isLoading = false;
        });
    } else if (this.itemType === SubPlanAssociationItemType.VEHICLE) {
      this.vehicleRepo
        .getVehicle(itemUid)
        .toPromise()
        .then((res: any) => {
          this.errorGetItemBadges = false;
          this.userBadgesloadingModel.isLoading = false;
          this.afterGetItemBadges(res);
        })
        .catch((error) => {
          this.errorGetItemBadges = true;
          this.userBadgesloadingModel.isLoading = false;
        });
    }
  }

  afterGetItemBadges(res: any) {
    const list: SubPlanAssociationUserBadgeModel[] = [];

    if (res.authSupports) {
      res.authSupports.forEach((b) => {
        const o = new SubPlanAssociationUserBadgeModel(b);
        list.push(o);
      });
    }

    this.setActualBadges(list);
  }

  searchUsers(value: string) {
    this.itemListloadingModel.isLoading = true;
    const params = this.getUsersParams(value);
    this.userRepo
      .getUsersList(this.itemOffsetList, this.itemLimitList, params)
      .toPromise()
      .then((res: UserList) => {
        this.errorGettingItems = false;
        this.itemListloadingModel.isLoading = false;
        this.afterGetItems(res);
      })
      .catch((error) => {
        this.errorGettingItems = true;
        this.itemListloadingModel.isLoading = false;
      });
  }

  getUsersParams(value: string): any[] {
    if (value === '') {
      return [
        { key: 'sortFieldName', value: 'LAST_NAME' },
        { key: 'sortDirection', value: 'ASC' },
      ];
    } else {
      return [
        { key: 'search', value: value },
      ];
    }
  }

  getVehiclesParams(value: string): any[] {
    if (value === '') {
      return [
        { key: 'sortFieldName', value: 'PLATE_NUMBER' },
        { key: 'sortDirection', value: 'ASC' },
      ];
    } else {
      return [
        { key: 'search', value: value },
      ];
    }
  }

  searchVehicles(value: string) {
    this.itemListloadingModel.isLoading = true;
    const params = this.getVehiclesParams(value);

    this.vehicleRepo
      .getVehicles(this.itemOffsetList, this.itemLimitList, params)
      .toPromise()
      .then((res: VehiclePagedList) => {
        this.errorGettingItems = false;
        this.itemListloadingModel.isLoading = false;
        this.afterGetItems(res);
      })
      .catch((error) => {
        this.errorGettingItems = true;
        this.itemListloadingModel.isLoading = false;
      });
  }

  searchBadges(value: string): void {
    this.badgeListloadingModel.isLoading = true;
    const params = this.getBadgesParams();
    this.badgeRepo
      .getBadgeStockList(this.badgeOffsetList, this.badgeLimitList, value)
      .toPromise()
      .then((res: BadgePageable) => {
        this.errorGettingBadges = false;
        this.badgeListloadingModel.isLoading = false;
        this.afterGetBadges(res);
      })
      .catch((error) => {
        this.errorGettingBadges = true;
        this.badgeListloadingModel.isLoading = false;
      });
  }
  getBadgesParams(): any[] {
    return [
      { key: 'sortFieldName', value: 'VALUE' },
      { key: 'sortDirection', value: 'ASC' },
      { key: 'availableBadgeOnly', value: true },
    ];
  }

  unassignBadgeToItem(badge: SubPlanAssociationBadgeModel): void {
    const body: AssignAuthSupportRequestBody = this.getAssignBadgeBody(badge);
    if (this.itemType === SubPlanAssociationItemType.USER) {
      this.badgeRepo2
          .unassignBadgeToUser(
              this.currentItem.uid,
              this.subscriptionPlan.uid,
              body
          )
          .toPromise()
          .then((res: any) => {
            this.errorAssignBadge = false;
            this.afterUnassignBadgeToItem();
          })
          .catch((error) => {
            this.errorAssignBadge = true;            
          });
    } else if (this.itemType === SubPlanAssociationItemType.VEHICLE) {
      this.badgeRepo2
          .unassignBadgeToVehicle(
              this.currentItem.uid,
              this.subscriptionPlan.uid,
              body
          )
          .toPromise()
          .then((res: any) => {
            this.errorAssignBadge = false;
            this.afterUnassignBadgeToItem();
          })
          .catch((error) => {
            this.errorAssignBadge = true;
          });
    }
  }
  assignBadgeToItem(badge: SubPlanAssociationBadgeModel): void {
    this.assignBadgeListloadingModel.isLoading = true;
    const body: AssignAuthSupportRequestBody = this.getAssignBadgeBody(badge);
    if (this.itemType === SubPlanAssociationItemType.USER) {
      this.badgeRepo2
        .assignBadgeToUser(
          this.currentItem.uid,
          this.subscriptionPlan.uid,
          body
        )
        .toPromise()
        .then((res: any) => {
          this.errorAssignBadge = false;
          this.assignBadgeListloadingModel.isLoading = false;
          this.afterAssignBadgeToItem();
        })
        .catch((error) => {          
          this.errorAssignBadge = true;          
          this.assignBadgeListloadingModel.isLoading = false;
        });
    } else if (this.itemType === SubPlanAssociationItemType.VEHICLE) {
      this.badgeRepo2
        .assignBadgeToVehicle(
          this.currentItem.uid,
          this.subscriptionPlan.uid,
          body
        )
        .toPromise()
        .then((res: any) => {
          this.errorAssignBadge = false;
          this.assignBadgeListloadingModel.isLoading = false;
          this.afterAssignBadgeToItem();
        })
        .catch((httpErrorResponse) => {
          this.errorAssignBadgeMsg = this.translate.instant(`error.${httpErrorResponse.error.code}`)
          this.errorAssignBadge = true;
          this.assignBadgeListloadingModel.isLoading = false;
        });
    }
  }
  getAssignBadgeBody(badge: SubPlanAssociationBadgeModel): AssignAuthSupportRequestBody {
    const body: AssignAuthSupportRequestBody = {
      authSupportsUid: [
        badge.uid
      ]
    };
    return body;
  }

  afterAssignBadgeToItem(): void {
    // clean badge search
    this.searchedBadge.reset();
    this.dataHasChanged.next(true);
    this.getItemBadges(this.currentItem.uid);
  }

  afterUnassignBadgeToItem() {
    this.dataHasChanged.next(true);
    this.getItemBadges(this.currentItem.uid);
  }

  addItemToSubPlan(item: SubPlanAssociationItemModel): void {
    this.addItemToSubPlanLoadingModel.isLoading = true;
    if (this.itemType === SubPlanAssociationItemType.USER) {
      const body: SubscribeUsersRequestBody = {
        usersUid: [item.uid]
      };
      this.subscriptionRepo
        .subscribeUsersToSubscription(this.subscriptionPlan.uid, body)
        .toPromise()
        .then((res: any) => {
          this.errorAddItemToSubPlan = false;
          this.addItemToSubPlanLoadingModel.isLoading = false;
          this.afterAddItemToSubPlan(item);
        })
        .catch((error) => {
          this.addItemToSubPlanLoadingModel.isLoading = false;
          // already linked
          if (error?.status === 409) {
            this.afterAddItemToSubPlan(item);
          } else {
            this.errorAddItemToSubPlan = true;
          }
        });
    } else if (this.itemType === SubPlanAssociationItemType.VEHICLE) {
      const body: AssignVehiclesRequestBody = {
        vehiclesUid: [item.uid]
      };
      this.subscriptionRepo
        .assignVehicleToSubscription(this.subscriptionPlan.uid, body)
        .toPromise()
        .then((res: any) => {
          this.errorAddItemToSubPlan = false;
          this.addItemToSubPlanLoadingModel.isLoading = false;
          this.afterAddItemToSubPlan(item);
        })
        .catch((error) => {
          this.addItemToSubPlanLoadingModel.isLoading = false;
          // already linked
          if (error?.status === 409) {
            this.afterAddItemToSubPlan(item);
          } else {
            this.errorAddItemToSubPlan = true;
          }
        });
    }
  }

  afterAddItemToSubPlan(item: SubPlanAssociationItemModel): void {
    this.getItemBadges(item.uid);
    this.dataHasChanged.next(true);
  }

  afterGetItems(res: any): void {
    const list = [];

    res?.list.forEach((u) => {
      const item = new SubPlanAssociationItemModel(
        this.itemType,
        u,
        this.subscriptionPlan
      );
      list.push(item);
    });

    this.items = list;
  }

  afterGetBadges(res: BadgePageable): void {
    const list: SubPlanAssociationBadgeModel[] = [];
    res?.data.forEach((b) => {
      const badge = new SubPlanAssociationBadgeModel(b, this.currentItem);
      list.push(badge);
    });

    this.badges = list;
  }

  // USER EVENTS
  onChangeSearchedItem(value: string): void {
    clearTimeout(this.timeoutSearchItem);
    this.clearBadges();
    this.currentItem = null;
    this.timeoutSearchItem = setTimeout(() => {
      if (value !== '') {
        this.valueWrited = value;
        this.searchItems(value);
      } else {
        this.items = [];
        this.searchedBadge.disable();
      }
    }, this.DELAY_SEARCH_ITEM_AFTER_KEYUP);
  }

  searchItems(value: string): void {
    if (this.itemType === SubPlanAssociationItemType.USER) {
      this.searchUsers(value);
    } else if (this.itemType === SubPlanAssociationItemType.VEHICLE) {
      this.searchVehicles(value);
    }
  }

  displayItem(item: SubPlanAssociationItemModel): string {
    return item && item.displayName ? item.displayName : '';
  }

  displayBadge(badge: SubPlanAssociationBadgeModel): string {
    return '';
  }

  clearBadges(): void {
    this.clearActualBadges();
    this.searchedBadge.disable();
  }

  setActualBadges(value: SubPlanAssociationUserBadgeModel[]): void {
    value.sort((a, b) => a.name.localeCompare(b.name));
    this.allActualBadges = value;
    this.searchActualBadges();
  }

  searchActualBadges(): void {
    const filteredList = this.allActualBadges.slice(
      this.actualBadgesOffsetList,
      this.actualBadgesOffsetList + this.actualBadgesLimitList
    );
    this.actualBadges = filteredList;
    this.createTableMapping();
  }

  clearActualBadges(): void {
    this.setActualBadges([]);
  }

  onClickItemOption(item: SubPlanAssociationItemModel): void {
    this.searchedBadge.enable();
    this.currentItem = item;
    this.addItemToSubPlan(item);
  }

  onClickBadgeOption(badge: SubPlanAssociationBadgeModel): void {
    this.addBadgeToItem(badge);
  }

  addBadgeToItem(badge: SubPlanAssociationBadgeModel) {
    this.assignBadgeToItem(badge);
  }

  removeBadge(badge: SubPlanAssociationBadgeModel) {
    this.unassignBadgeToItem(badge);
  }

  onChangeSearchedBadge(value: string): void {
    if (value !== '') {
      this.searchBadges(value);
    } else {
      this.badges = [];
    }
  }

  /***************************************  TABLE MAPPING  **********************************/
  createTableMapping() {
    const table: TableModel = this.tableSubPlanAssociationBadgesMapper.init(
      this.actualBadges
    );
    this.badgeTable = table;
    this.createPagingMapping();
  }

  onEventOnClick(event: any) {
    if (
      event.cell.columnId ===
        this.tableSubPlanAssociationBadgesMapper.COLUMN_ID_ACTION &&
      event.target.dataId ===
        this.tableSubPlanAssociationBadgesMapper.CELL_ID_DELETE_BADGE
    ) {
      const badge: SubPlanAssociationBadgeModel = event.row.obj;
      this.removeBadge(badge);
    } else {
      return;
    }
  }

  createPagingMapping() {
    this.paging = this.generatePaging();
  }

  generatePaging(): PagingModel {
    const totalBadges = this.allActualBadges.length;
    const nextLink =
      this.actualBadgesOffsetList + this.actualBadgesLimitList < totalBadges
        ? 'ok'
        : null;
    const prevLink = this.actualBadgesOffsetList > 0 ? 'ok' : null;
    const curItems =
      this.actualBadges.length >= this.actualBadgesLimitList
        ? this.actualBadgesLimitList
        : totalBadges - this.actualBadgesOffsetList;
    const paging = new PagingModel(
      this.actualBadgesOffsetList,
      this.actualBadgesLimitList,
      curItems,
      totalBadges,
      nextLink,
      prevLink
    );

    // configuration
    paging.configuration.actionIfNoLink = 'disabled';
    paging.configuration.pagingEnabled = false;
    paging.configuration.rangeEnabled = true;
    // prevLink
    const prevLinkElement = new UiIcon();
    prevLinkElement.type = IconType.FONTAWESOME;
    prevLinkElement.value = 'icon-arrow-left-circle';
    paging.configuration.prevLinkElement = prevLinkElement;
    // nextLink
    const nextLinkElement = new UiIcon();
    nextLinkElement.type = IconType.FONTAWESOME;
    nextLinkElement.value = 'icon-arrow-right-circle';
    paging.configuration.nextLinkElement = nextLinkElement;

    return paging;
  }

  eventOnClickPrevLink(event: LinkClickSateType): void {
    if (event === LinkClickSateType.ALLOWED) {
      this.onPagination('prev-event');
    }
  }

  eventOnClickNextLink(event: LinkClickSateType) {
    if (event === LinkClickSateType.ALLOWED) {
      this.onPagination('next-event');
    }
  }

  // TODO: refacto to Paging Component
  onPagination(type: 'next' | 'prev' | 'next-event' | 'prev-event') {
    if (type === 'next') {
      // this.onNextPaginationGroup();
    } else if (type === 'prev') {
      // this.onPrevPaginationGroup();
    } else if (type === 'next-event') {
      this.onNextPaginationEvent();
    } else if (type === 'prev-event') {
      this.onPrevPaginationEvent();
    }
  }

  onPrevPaginationEvent() {
    this.actualBadgesOffsetList =
      this.actualBadgesOffsetList - this.actualBadgesLimitList;
    this.searchActualBadges();
  }

  onNextPaginationEvent() {
    this.actualBadgesOffsetList =
      this.actualBadgesOffsetList + this.actualBadgesLimitList;
    this.searchActualBadges();
  }
}
